import { Component, ElementRef, OnDestroy, ViewChild, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import type { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import {
  IonBadge,
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonSearchbar,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { PosProduct, PosProductAddon } from '@adminpages/domain-models';
import { PosClient, PosOrderCustomerPayload } from '@adminpages/pos-api';
import { PosPrintingService } from '@adminpages/pos-printing';
import { PosStoreService } from '@adminpages/pos-state';
import { PaymentMethodSelectorComponent } from '@adminpages/payments-ui';

@Component({
  selector: 'foco-pos-sale-page',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    IonBadge,
    IonButton,
    IonButtons,
    IonChip,
    IonContent,
    IonFooter,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonModal,
    IonSearchbar,
    IonText,
    IonTitle,
    IonToolbar,
    PaymentMethodSelectorComponent,
  ],
  template: `
    <ion-header class="pos-header">
      <ion-toolbar>
        <ion-title>
          <span>{{ store.selectedWorkspace().name }}</span>
          <small>{{ store.selectedCashRegister()?.name || 'Caja' }}</small>
        </ion-title>
        <ion-buttons slot="end">
          @if (store.selectedWorkspace().capabilities.repairs) {
            <ion-button routerLink="/repairs" aria-label="Taller" title="Taller">
              <ion-icon name="construct-outline" slot="icon-only"></ion-icon>
            </ion-button>
          }
          <ion-button routerLink="/pos/recent">
            <ion-icon name="receipt-outline" slot="start"></ion-icon>
            Ventas
          </ion-button>
          <ion-button routerLink="/cash-session" [queryParams]="{ scope: 'pos', return: '/pos' }">
            <ion-icon name="cash-outline" slot="start"></ion-icon>
            Caja
          </ion-button>
          <ion-button routerLink="/settings">
            <ion-icon name="cog-outline" slot="icon-only"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="pos-page sale-content">
      <main class="sale-shell">
        <section class="catalog-panel">
          <div class="sale-toolbar">
            <ion-searchbar
              [ngModel]="store.searchQuery()"
              (ngModelChange)="store.setSearch($event)"
              placeholder="Buscar por nombre, SKU o barcode"
              show-clear-button="focus"
            ></ion-searchbar>
            <ion-button class="scanner-button" (click)="openScanner()">
              <ion-icon name="barcode-outline" slot="start"></ion-icon>
              Scanner
            </ion-button>
          </div>

          <div class="category-strip">
            @for (category of visibleCategories(); track category.slug) {
              <ion-chip
                [class.selected]="category.slug === store.activeCategory()"
                (click)="store.setCategory(category.slug)"
              >
                {{ category.name }}
              </ion-chip>
            }
          </div>

          @if (store.catalogLoading()) {
            <div class="catalog-message">Cargando productos...</div>
          }

          @if (store.errorMessage(); as error) {
            <div class="catalog-message error">{{ error }}</div>
          }

          <div class="product-grid">
            @for (product of store.visibleProducts(); track product.id) {
              <button type="button" class="product-card" (click)="openProduct(product)">
                <span class="product-art">
                  <ion-icon [name]="product.type === 'bundle' ? 'bag-handle-outline' : 'grid-outline'"></ion-icon>
                </span>
                <span class="product-body">
                  <span class="product-type">{{ productTypeLabel(product.type) }}</span>
                  <strong>{{ product.name }}</strong>
                  <small>{{ product.sku }}</small>
                </span>
                <span class="product-footer">
                  <strong class="money">{{ store.money(product.price) }}</strong>
                  @if (product.trackStock) {
                    <ion-badge [color]="product.stockQuantity > 1 ? 'success' : 'warning'">
                      Stock {{ product.stockQuantity }}
                    </ion-badge>
                  }
                </span>
              </button>
            } @empty {
              <div class="empty-products">
                <ion-icon name="search-outline"></ion-icon>
                <strong>Sin productos</strong>
                <span>Prueba otra busqueda o categoria.</span>
              </div>
            }
          </div>
        </section>

        <aside class="cart-panel">
          <header>
            <div>
              <p>Carrito</p>
              <h2>{{ store.cartCount() }} piezas</h2>
            </div>
            <ion-button fill="clear" color="danger" size="small" (click)="store.clearCart()">
              Limpiar
            </ion-button>
          </header>

          <div class="cart-items">
            @for (item of store.cart(); track item.lineId) {
              <article class="cart-item">
                <div>
                  <strong>{{ item.product.name }}</strong>
                  <small>{{ item.product.sku }}</small>
                  @if (item.addons.length > 0) {
                    <span class="addon-list">
                      @for (addon of item.addons; track addon.id) {
                        + {{ addon.name }}
                      }
                    </span>
                  }
                </div>
                <div class="quantity-controls">
                  <ion-button size="small" fill="outline" (click)="store.decrement(item.lineId)">
                    <ion-icon name="remove"></ion-icon>
                  </ion-button>
                  <span>{{ item.quantity }}</span>
                  <ion-button size="small" fill="outline" (click)="store.increment(item.lineId)">
                    <ion-icon name="add"></ion-icon>
                  </ion-button>
                </div>
                <strong class="money">{{ store.money(store.itemTotal(item)) }}</strong>
                <ion-button color="danger" fill="clear" size="small" (click)="store.remove(item.lineId)">
                  <ion-icon name="trash-outline"></ion-icon>
                </ion-button>
              </article>
            } @empty {
              <div class="empty-cart">
                <ion-icon name="bag-handle-outline"></ion-icon>
                <strong>Agrega productos</strong>
                <span>Usa el buscador, categorias o scanner.</span>
              </div>
            }
          </div>

          <footer>
            <div class="total-row">
              <span>Total</span>
              <strong class="money">{{ store.money(store.cartSubtotal()) }}</strong>
            </div>
            <ion-button expand="block" size="large" [disabled]="store.cart().length === 0 || store.checkoutLoading()" (click)="openCheckout()">
              Cobrar
            </ion-button>
            <ion-button expand="block" fill="outline" [disabled]="store.cart().length === 0 || store.checkoutLoading()" (click)="saveWithoutPayment()">
              Guardar sin pago
            </ion-button>
          </footer>
        </aside>
      </main>
    </ion-content>

    <div class="mobile-cart-footer">
      <button type="button" class="mobile-cart-bar" (click)="mobileCartOpen.set(true)">
        <span>
          <small>{{ store.cartCount() }} piezas</small>
          <strong class="money">{{ store.money(store.cartSubtotal()) }}</strong>
        </span>
        <ion-button size="small" [disabled]="store.cart().length === 0 || store.checkoutLoading()" (click)="openCheckout(); $event.stopPropagation()">
          Cobrar
        </ion-button>
      </button>
    </div>

    <ion-modal
      cssClass="mobile-cart-modal"
      [isOpen]="mobileCartOpen() && !productOpen() && !checkoutOpen()"
      (didDismiss)="mobileCartOpen.set(false)"
    >
      <ng-template>
        <ion-header>
          <ion-toolbar>
            <ion-title>Carrito</ion-title>
            <ion-buttons slot="end">
              <ion-button (click)="mobileCartOpen.set(false)">
                <ion-icon name="close" slot="icon-only"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>

        <ion-content class="mobile-cart-content">
          <section class="mobile-cart-full">
            <header>
              <div>
                <p>Carrito</p>
                <h2>{{ store.cartCount() }} piezas</h2>
              </div>
              <ion-button fill="clear" color="danger" size="small" (click)="store.clearCart()">
                Limpiar
              </ion-button>
            </header>

            <div class="cart-items mobile-modal-items">
              @for (item of store.cart(); track item.lineId) {
                <article class="cart-item">
                  <div>
                    <strong>{{ item.product.name }}</strong>
                    <small>{{ item.product.sku }}</small>
                    @if (item.addons.length > 0) {
                      <span class="addon-list">
                        @for (addon of item.addons; track addon.id) {
                          + {{ addon.name }}
                        }
                      </span>
                    }
                  </div>
                  <div class="quantity-controls">
                    <ion-button size="small" fill="outline" (click)="store.decrement(item.lineId)">
                      <ion-icon name="remove"></ion-icon>
                    </ion-button>
                    <span>{{ item.quantity }}</span>
                    <ion-button size="small" fill="outline" (click)="store.increment(item.lineId)">
                      <ion-icon name="add"></ion-icon>
                    </ion-button>
                  </div>
                  <strong class="money">{{ store.money(store.itemTotal(item)) }}</strong>
                  <ion-button color="danger" fill="clear" size="small" (click)="store.remove(item.lineId)">
                    <ion-icon name="trash-outline"></ion-icon>
                  </ion-button>
                </article>
              } @empty {
                <div class="empty-cart">
                  <ion-icon name="bag-handle-outline"></ion-icon>
                  <strong>Agrega productos</strong>
                  <span>Usa el buscador, categorias o scanner.</span>
                </div>
              }
            </div>
          </section>
        </ion-content>

        <ion-footer>
          <ion-toolbar>
            <div class="mobile-cart-actions">
              <div class="total-row">
                <span>Total</span>
                <strong class="money">{{ store.money(store.cartSubtotal()) }}</strong>
              </div>
              <ion-button expand="block" size="large" [disabled]="store.cart().length === 0 || store.checkoutLoading()" (click)="openCheckout()">
                Cobrar
              </ion-button>
              <ion-button expand="block" fill="outline" [disabled]="store.cart().length === 0 || store.checkoutLoading()" (click)="saveWithoutPayment()">
                Guardar sin pago
              </ion-button>
            </div>
          </ion-toolbar>
        </ion-footer>
      </ng-template>
    </ion-modal>

    <ion-modal [isOpen]="productOpen()" (didDismiss)="closeProduct()">
      <ng-template>
        @if (selectedProduct(); as product) {
          <ion-header>
            <ion-toolbar>
              <ion-title>{{ product.name }}</ion-title>
              <ion-buttons slot="end">
                <ion-button (click)="closeProduct()">
                  <ion-icon name="close" slot="icon-only"></ion-icon>
                </ion-button>
              </ion-buttons>
            </ion-toolbar>
          </ion-header>
          <ion-content class="detail-content">
            <section class="detail-summary">
              <span class="product-art large">
                <ion-icon [name]="product.type === 'bundle' ? 'bag-handle-outline' : 'grid-outline'"></ion-icon>
              </span>
              <div>
                <p>{{ product.sku }}</p>
                <h2>{{ store.money(product.price) }}</h2>
                <span>{{ productTypeLabel(product.type) }}</span>
              </div>
            </section>

            <ion-item>
              <ion-label position="stacked">Cantidad</ion-label>
              <ion-input type="number" min="1" [(ngModel)]="selectedQuantity"></ion-input>
            </ion-item>

            @if (product.addons.length > 0) {
              <section class="addons-section">
                <h3>Addons</h3>
                @for (addon of product.addons; track addon.id) {
                  <button
                    type="button"
                    class="addon-option"
                    [class.selected]="isAddonSelected(addon)"
                    (click)="toggleAddon(addon)"
                  >
                    <span>{{ addon.name }}</span>
                    <strong>{{ store.money(addon.priceDelta) }}</strong>
                  </button>
                }
              </section>
            }

            @if (product.bundleComponents.length > 0) {
              <section class="bundle-section">
                <h3>Incluye</h3>
                @for (component of product.bundleComponents; track component.name) {
                  <div>
                    <span>{{ component.name }}</span>
                    <strong>{{ component.quantity }} {{ component.unit }}</strong>
                  </div>
                }
              </section>
            }
          </ion-content>
          <ion-footer>
            <ion-toolbar>
              <ion-button expand="block" size="large" (click)="addSelectedProduct()">
                Agregar {{ selectedQuantity || 1 }}
              </ion-button>
            </ion-toolbar>
          </ion-footer>
        }
      </ng-template>
    </ion-modal>

    <ion-modal cssClass="checkout-modal" [isOpen]="checkoutOpen()" (didDismiss)="checkoutOpen.set(false)">
      <ng-template>
        <ion-header>
          <ion-toolbar>
            <ion-title>Cobro</ion-title>
            <ion-buttons slot="end">
              <ion-button (click)="checkoutOpen.set(false)">
                <ion-icon name="close" slot="icon-only"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content class="checkout-content">
          <section class="checkout-total">
            <span>Total venta</span>
            <strong class="money">{{ store.money(store.cartSubtotal()) }}</strong>
          </section>

          <section class="checkout-customer">
            <div>
              <span>Cliente</span>
              <strong>{{ customerLabel() }}</strong>
            </div>
            <ion-button fill="outline" size="small" (click)="customerOpen.set(true)">
              Cambiar
            </ion-button>
          </section>

          <foco-payment-method-selector [method]="paymentMethod()" (methodChange)="paymentMethod.set($event)"></foco-payment-method-selector>

          <ion-item>
            <ion-label position="stacked">Monto recibido</ion-label>
            <ion-input type="number" [ngModel]="paymentAmount()" (ngModelChange)="paymentAmount.set(+$event)"></ion-input>
          </ion-item>

          <div class="checkout-change">
            <span>{{ paymentAmount() >= store.cartSubtotal() ? 'Cambio' : 'Saldo' }}</span>
            <strong class="money">
              {{ store.money(abs(paymentAmount() - store.cartSubtotal())) }}
            </strong>
          </div>
        </ion-content>
        <ion-footer>
          <ion-toolbar>
            @if (store.errorMessage(); as error) {
              <ion-text color="danger">{{ error }}</ion-text>
            }
            <ion-button expand="block" size="large" [disabled]="store.checkoutLoading()" (click)="completeSale()">
              {{ store.checkoutLoading() ? 'Guardando...' : paymentAmount() >= store.cartSubtotal() ? 'Cobrar y confirmar' : 'Registrar anticipo' }}
            </ion-button>
          </ion-toolbar>
        </ion-footer>
      </ng-template>
    </ion-modal>

    <ion-modal cssClass="customer-modal" [isOpen]="customerOpen()" (didDismiss)="customerOpen.set(false)">
      <ng-template>
        <ion-header>
          <ion-toolbar>
            <ion-title>Cliente</ion-title>
            <ion-buttons slot="end">
              <ion-button (click)="customerOpen.set(false)">
                <ion-icon name="close" slot="icon-only"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>

        <ion-content class="customer-content">
          <section class="customer-default">
            <span>Default POS</span>
            <strong>Publico en general</strong>
            <small>Se usara si no capturas datos del cliente.</small>
          </section>

          <section class="customer-search">
            <ion-searchbar
              [ngModel]="customerSearchQuery()"
              (ngModelChange)="customerSearchQuery.set($event)"
              placeholder="Buscar cliente por nombre, telefono o email"
              show-clear-button="focus"
            ></ion-searchbar>
            <ion-button expand="block" fill="outline" [disabled]="customerSearchLoading() || customerSearchQuery().trim().length < 2" (click)="searchCustomers()">
              {{ customerSearchLoading() ? 'Buscando...' : 'Buscar cliente' }}
            </ion-button>
          </section>

          @if (customerSearchMessage()) {
            <section class="customer-message">{{ customerSearchMessage() }}</section>
          }

          @if (customerSearchResults().length > 0) {
            <section class="customer-results">
              @for (client of customerSearchResults(); track client.id) {
                <button type="button" class="customer-result" (click)="selectCustomer(client)">
                  <div>
                    <strong>{{ client.name }}</strong>
                    <small>{{ client.phone || 'Sin telefono' }}{{ client.email ? ' · ' + client.email : '' }}</small>
                  </div>
                  <ion-icon name="chevron-forward"></ion-icon>
                </button>
              }
            </section>
          }

          @if (selectedCustomer(); as client) {
            <section class="selected-customer">
              <span>Cliente seleccionado</span>
              <strong>{{ client.name }}</strong>
              <small>{{ client.phone || client.email || 'Sin contacto' }}</small>
            </section>
          }

          <ion-item>
            <ion-label position="stacked">Nombre</ion-label>
            <ion-input [ngModel]="customerName()" (ngModelChange)="updateCustomerField('name', $event)" placeholder="Nombre del cliente"></ion-input>
          </ion-item>

          <ion-item>
            <ion-label position="stacked">Telefono</ion-label>
            <ion-input [ngModel]="customerPhone()" (ngModelChange)="updateCustomerField('phone', $event)" placeholder="10 digitos"></ion-input>
          </ion-item>

          <ion-item>
            <ion-label position="stacked">Email</ion-label>
            <ion-input type="email" [ngModel]="customerEmail()" (ngModelChange)="updateCustomerField('email', $event)" placeholder="cliente@email.com"></ion-input>
          </ion-item>

          <ion-item>
            <ion-label position="stacked">Notas</ion-label>
            <ion-input [ngModel]="customerNotes()" (ngModelChange)="updateCustomerField('notes', $event)" placeholder="Referencia opcional"></ion-input>
          </ion-item>
        </ion-content>

        <ion-footer>
          <ion-toolbar>
            <div class="customer-actions">
              <ion-button fill="outline" color="medium" (click)="clearCustomer()">
                Usar default
              </ion-button>
              <ion-button [disabled]="customerSaving()" (click)="saveCustomer()">
                {{ customerSaving() ? 'Guardando...' : selectedCustomer() ? 'Usar cliente' : 'Crear cliente' }}
              </ion-button>
            </div>
          </ion-toolbar>
        </ion-footer>
      </ng-template>
    </ion-modal>

    <ion-modal cssClass="scanner-modal" [isOpen]="scannerOpen()" (didDismiss)="closeScanner()">
      <ng-template>
        <ion-header>
          <ion-toolbar>
            <ion-title>Scanner</ion-title>
            <ion-buttons slot="end">
              <ion-button (click)="closeScanner()">
                <ion-icon name="close" slot="icon-only"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>

        <ion-content class="scanner-content">
          <section class="scanner-camera">
            <video #scannerPreview muted playsinline></video>
            <span class="scanner-frame"></span>
          </section>

          <section class="scanner-panel">
            <div>
              <span>Codigo</span>
              <strong>{{ scannerManualCode() || 'Esperando lectura' }}</strong>
              @if (scannerMessage()) {
                <small>{{ scannerMessage() }}</small>
              }
            </div>
          </section>

          <section class="scanner-manual">
            <ion-item>
              <ion-label position="stacked">Captura manual</ion-label>
              <ion-input
                [ngModel]="scannerManualCode()"
                (ngModelChange)="scannerManualCode.set($event)"
                placeholder="SKU o barcode"
              ></ion-input>
            </ion-item>
            <ion-button expand="block" [disabled]="scannerLoading() || !scannerManualCode().trim()" (click)="submitManualScan()">
              {{ scannerLoading() ? 'Buscando...' : 'Buscar producto' }}
            </ion-button>
          </section>

          @if (scannerMatches().length > 1) {
            <section class="scanner-results">
              <h3>Selecciona producto</h3>
              @for (product of scannerMatches(); track product.id) {
                <button type="button" class="scanner-result" (click)="selectScannedProduct(product)">
                  <div>
                    <strong>{{ product.name }}</strong>
                    <small>{{ product.sku }}{{ product.barcode ? ' · ' + product.barcode : '' }}</small>
                  </div>
                  <span>{{ store.money(product.price) }}</span>
                </button>
              }
            </section>
          }
        </ion-content>
      </ng-template>
    </ion-modal>
  `,
  styles: `
    .pos-header ion-toolbar {
      --background: var(--pos-color-surface);
      --border-color: var(--pos-color-border);
    }

    ion-title span,
    ion-title small {
      display: block;
    }

    ion-title small {
      color: var(--pos-color-muted);
      font-size: 12px;
      font-weight: 500;
    }

    .sale-content {
      --overflow: hidden;
      --padding-bottom: 18px;
      --padding-end: 18px;
      --padding-start: 18px;
      --padding-top: 18px;
    }

    .sale-shell {
      display: grid;
      gap: 18px;
      grid-template-columns: minmax(0, 1fr) clamp(300px, 32vw, 390px);
      height: calc(100vh - 104px);
      margin: 0 auto;
      max-width: 1440px;
      min-height: 0;
    }

    @supports (height: 100svh) {
      .sale-shell {
        height: calc(100svh - 104px);
      }
    }

    @supports (height: 100dvh) {
      .sale-shell {
        height: calc(100dvh - 104px);
      }
    }

    .catalog-panel,
    .cart-panel {
      background: var(--pos-color-surface);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }

    .catalog-panel {
      display: grid;
      grid-template-rows: auto auto auto minmax(0, 1fr);
      padding: 14px;
    }

    .sale-toolbar {
      align-items: center;
      display: grid;
      gap: 10px;
      grid-template-columns: minmax(0, 1fr) auto;
    }

    ion-searchbar {
      --background: var(--pos-color-input);
      --color: var(--pos-color-text);
      --placeholder-color: var(--pos-color-muted);
      --box-shadow: none;
      --border-radius: 8px;
      padding: 0;
    }

    .scanner-button {
      min-height: 48px;
    }

    .category-strip {
      display: flex;
      gap: 8px;
      overflow-x: auto;
      padding: 14px 0;
    }

    ion-chip {
      --background: var(--pos-color-input);
      --color: var(--pos-color-primary);
      border: 1px solid var(--pos-color-border);
      color: var(--pos-color-primary);
      cursor: pointer;
      flex: 0 0 auto;
      font-weight: 700;
    }

    :host-context([data-pos-theme-mode='dark']) ion-chip {
      border-color: color-mix(in srgb, var(--pos-color-primary), transparent 42%);
    }

    ion-chip.selected {
      --background: var(--pos-color-primary);
      color: white;
    }

    .product-grid {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(auto-fill, minmax(205px, 1fr));
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
      padding-right: 4px;
      scroll-behavior: smooth;
      align-content: start;
    }

    .catalog-message {
      background: var(--pos-color-input);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      color: var(--pos-color-muted);
      margin-bottom: 10px;
      padding: 10px 12px;
    }

    .catalog-message.error {
      color: var(--ion-color-danger);
    }

    .product-grid::-webkit-scrollbar,
    .cart-items::-webkit-scrollbar {
      width: 8px;
    }

    .product-grid::-webkit-scrollbar-thumb,
    .cart-items::-webkit-scrollbar-thumb {
      background: rgba(46, 98, 255, 0.28);
      border-radius: 999px;
    }

    .product-grid::-webkit-scrollbar-track,
    .cart-items::-webkit-scrollbar-track {
      background: transparent;
    }

    .product-card {
      background: var(--pos-color-surface);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      color: var(--pos-color-text);
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: 12px;
      min-height: 240px;
      padding: 12px;
      text-align: left;
    }

    .product-card:hover {
      border-color: rgba(46, 98, 255, 0.62);
      box-shadow: 0 10px 28px rgba(24, 34, 48, 0.1);
    }

    .product-art {
      align-items: center;
      background: linear-gradient(135deg, rgba(46, 98, 255, 0.16), rgba(13, 185, 150, 0.16));
      border-radius: 8px;
      color: var(--pos-color-primary);
      display: flex;
      font-size: 42px;
      height: clamp(120px, 15vw, 168px);
      justify-content: center;
      width: 100%;
    }

    .product-art.large {
      aspect-ratio: 1;
      height: 120px;
      width: 120px;
    }

    .product-body {
      display: grid;
      gap: 4px;
      flex: 1;
      align-content: start;
      min-height: 0;
    }

    .product-body strong {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      line-height: 1.2;
      min-height: 2.4em;
    }

    .product-body small,
    .product-type {
      color: var(--pos-color-muted);
      font-size: 12px;
    }

    .product-body small {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .product-type {
      color: var(--pos-color-primary);
      font-weight: 800;
      line-height: 1;
      text-transform: uppercase;
    }

    .product-footer {
      align-items: center;
      display: grid;
      flex-wrap: wrap;
      gap: 8px;
      grid-template-columns: minmax(0, 1fr) auto;
      margin-top: auto;
      min-height: 34px;
    }

    .product-footer .money {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .cart-panel {
      display: grid;
      grid-template-rows: auto minmax(0, 1fr) auto;
    }

    .cart-panel header,
    .cart-panel footer {
      padding: 16px;
    }

    .cart-panel header {
      align-items: center;
      border-bottom: 1px solid var(--pos-color-border);
      display: flex;
      justify-content: space-between;
    }

    .cart-panel header p,
    .cart-panel header h2 {
      margin: 0;
    }

    .cart-panel header p {
      color: var(--pos-color-muted);
      font-size: 13px;
    }

    .cart-items {
      display: grid;
      gap: 10px;
      overflow: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
      padding: 12px;
      align-content: start;
    }

    .cart-item {
      align-items: center;
      background: var(--pos-color-input);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      display: grid;
      gap: 8px;
      grid-template-columns: minmax(0, 1fr) auto;
      padding: 10px;
    }

    .cart-item small,
    .addon-list {
      color: var(--pos-color-muted);
      display: block;
      font-size: 12px;
    }

    .quantity-controls {
      align-items: center;
      display: flex;
      gap: 6px;
      grid-column: 1 / -1;
    }

    .quantity-controls span {
      font-weight: 800;
      min-width: 28px;
      text-align: center;
    }

    .total-row {
      align-items: center;
      display: flex;
      justify-content: space-between;
      margin-bottom: 12px;
    }

    .total-row strong {
      font-size: 28px;
    }

    .empty-products,
    .empty-cart {
      align-content: center;
      color: var(--pos-color-muted);
      display: grid;
      gap: 8px;
      justify-items: center;
      min-height: 260px;
      text-align: center;
    }

    .empty-products ion-icon,
    .empty-cart ion-icon {
      font-size: 42px;
    }

    .mobile-cart-footer {
      display: none;
    }

    .mobile-cart-bar {
      align-items: center;
      background: var(--pos-color-surface);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      box-shadow: 0 14px 34px var(--pos-color-shadow);
      color: var(--pos-color-text);
      display: flex;
      justify-content: space-between;
      min-height: 72px;
      padding: 10px 12px;
      text-align: left;
      width: 100%;
    }

    .mobile-cart-bar small,
    .mobile-cart-bar strong {
      display: block;
    }

    .mobile-cart-bar small {
      color: var(--pos-color-muted);
      font-size: 12px;
      font-weight: 700;
    }

    .mobile-cart-bar strong {
      font-size: 22px;
    }

    .mobile-cart-full {
      display: grid;
      height: 100%;
      min-height: 0;
    }

    .mobile-cart-full header {
      padding: 16px;
    }

    .mobile-cart-full header {
      align-items: center;
      border-bottom: 1px solid var(--pos-color-border);
      display: flex;
      justify-content: space-between;
    }

    .mobile-cart-full header p,
    .mobile-cart-full header h2 {
      margin: 0;
    }

    .mobile-cart-full header p {
      color: var(--pos-color-muted);
      font-size: 13px;
    }

    .mobile-cart-content {
      --padding-bottom: 0;
      --padding-end: 0;
      --padding-start: 0;
      --padding-top: 0;
    }

    .mobile-modal-items {
      min-height: 0;
    }

    .mobile-cart-actions {
      padding: 12px 16px 16px;
    }

    .detail-content,
    .checkout-content {
      --padding-bottom: 18px;
      --padding-end: 18px;
      --padding-start: 18px;
      --padding-top: 18px;
    }

    ion-modal ion-toolbar {
      --background: var(--pos-color-surface);
      --border-color: var(--pos-color-border);
      --color: var(--pos-color-text);
    }

    ion-modal.checkout-modal {
      --height: min(760px, 92vh);
      --width: min(620px, 94vw);
    }

    .detail-summary {
      align-items: center;
      display: flex;
      gap: 18px;
      margin-bottom: 16px;
    }

    .detail-summary p,
    .detail-summary h2 {
      margin: 0;
    }

    .detail-summary p,
    .detail-summary span {
      color: var(--pos-color-muted);
    }

    .addons-section,
    .bundle-section {
      display: grid;
      gap: 10px;
      margin-top: 18px;
    }

    .addon-option,
    .bundle-section div {
      align-items: center;
      background: var(--pos-color-input);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      color: var(--pos-color-text);
      display: flex;
      justify-content: space-between;
      padding: 14px;
    }

    .addon-option.selected {
      border-color: var(--pos-color-primary);
      box-shadow: inset 0 0 0 1px var(--pos-color-primary);
    }

    .checkout-total {
      background: var(--pos-color-text);
      border-radius: 8px;
      color: var(--pos-color-background);
      display: grid;
      gap: 6px;
      margin-bottom: 16px;
      padding: 18px;
    }

    :host-context([data-pos-theme-mode='dark']) .checkout-total {
      background: var(--pos-color-input);
      border: 1px solid var(--pos-color-border);
      color: var(--pos-color-text);
    }

    .checkout-total strong {
      font-size: 42px;
    }

    .checkout-total small {
      opacity: 0.82;
    }

    .checkout-customer {
      align-items: center;
      background: var(--pos-color-input);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      display: flex;
      gap: 12px;
      justify-content: space-between;
      margin-bottom: 16px;
      padding: 10px 12px;
    }

    .checkout-customer div {
      display: grid;
      gap: 2px;
      min-width: 0;
    }

    .checkout-customer span,
    .checkout-customer strong {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .checkout-customer span {
      color: var(--pos-color-muted);
      font-size: 11px;
      font-weight: 800;
      text-transform: uppercase;
    }

    .checkout-change {
      align-items: center;
      background: var(--pos-color-input);
      border-radius: 8px;
      display: flex;
      justify-content: space-between;
      margin-top: 16px;
      padding: 16px;
    }

    .checkout-change strong {
      font-size: 24px;
    }

    .customer-content {
      --padding-bottom: 18px;
      --padding-end: 18px;
      --padding-start: 18px;
      --padding-top: 18px;
    }

    .customer-default {
      background: var(--pos-color-input);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      display: grid;
      gap: 4px;
      margin-bottom: 14px;
      padding: 14px;
    }

    .customer-default span,
    .customer-default small {
      color: var(--pos-color-muted);
    }

    .customer-search {
      display: grid;
      gap: 10px;
      margin-bottom: 14px;
    }

    .customer-message,
    .selected-customer,
    .customer-result {
      background: var(--pos-color-input);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
    }

    .customer-message {
      color: var(--pos-color-muted);
      margin-bottom: 12px;
      padding: 10px 12px;
    }

    .customer-results {
      display: grid;
      gap: 8px;
      margin-bottom: 14px;
    }

    .customer-result {
      align-items: center;
      color: var(--pos-color-text);
      cursor: pointer;
      display: grid;
      gap: 10px;
      grid-template-columns: minmax(0, 1fr) auto;
      padding: 12px;
      text-align: left;
      width: 100%;
    }

    .customer-result small,
    .selected-customer span,
    .selected-customer small {
      color: var(--pos-color-muted);
    }

    .customer-result strong,
    .customer-result small,
    .selected-customer span,
    .selected-customer strong,
    .selected-customer small {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .selected-customer {
      display: grid;
      gap: 4px;
      margin-bottom: 14px;
      padding: 12px;
    }

    .customer-actions {
      display: grid;
      gap: 10px;
      grid-template-columns: 1fr 1fr;
      padding: 8px 12px;
    }

    ion-modal.scanner-modal {
      --height: min(760px, 92vh);
      --width: min(620px, 94vw);
    }

    .scanner-content {
      --padding-bottom: 18px;
      --padding-end: 18px;
      --padding-start: 18px;
      --padding-top: 18px;
    }

    .scanner-camera {
      background: #111827;
      border-radius: 8px;
      display: grid;
      margin-bottom: 14px;
      min-height: 260px;
      overflow: hidden;
      place-items: center;
      position: relative;
    }

    .scanner-camera video {
      height: 100%;
      min-height: 260px;
      object-fit: cover;
      width: 100%;
    }

    .scanner-frame {
      border: 2px solid var(--pos-color-primary);
      border-radius: 8px;
      box-shadow: 0 0 0 999px rgba(0, 0, 0, 0.32);
      height: 42%;
      left: 12%;
      pointer-events: none;
      position: absolute;
      top: 29%;
      width: 76%;
    }

    .scanner-panel,
    .scanner-result {
      background: var(--pos-color-input);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
    }

    .scanner-panel {
      display: grid;
      gap: 4px;
      margin-bottom: 14px;
      padding: 12px;
    }

    .scanner-panel span,
    .scanner-panel small,
    .scanner-result small {
      color: var(--pos-color-muted);
    }

    .scanner-panel strong,
    .scanner-panel span,
    .scanner-panel small,
    .scanner-result strong,
    .scanner-result small {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .scanner-manual {
      display: grid;
      gap: 10px;
      margin-bottom: 16px;
    }

    .scanner-results {
      display: grid;
      gap: 10px;
    }

    .scanner-results h3 {
      margin: 0;
    }

    .scanner-result {
      align-items: center;
      color: var(--pos-color-text);
      cursor: pointer;
      display: grid;
      gap: 12px;
      grid-template-columns: minmax(0, 1fr) auto;
      padding: 12px;
      text-align: left;
    }

    .scanner-result span {
      color: var(--pos-color-primary);
      font-weight: 800;
      white-space: nowrap;
    }

    @media (max-width: 760px) {
      ion-title {
        text-align: left;
      }

      .sale-content {
        --overflow: auto;
      }

      .sale-shell {
        grid-template-columns: 1fr;
        height: auto;
        min-height: auto;
        padding-bottom: 88px;
      }

      .cart-panel {
        display: none;
      }

      .mobile-cart-footer {
        display: block;
        inset: auto 10px 10px;
        padding-bottom: env(safe-area-inset-bottom);
        pointer-events: none;
        position: fixed;
        z-index: 30;
      }

      .mobile-cart-bar {
        pointer-events: auto;
      }

      .catalog-panel {
        height: auto;
        min-height: auto;
        overflow: visible;
      }

      .product-grid {
        overflow: visible;
        padding-right: 0;
      }
    }

    @media (max-width: 620px) {
      .sale-content {
        --padding-end: 10px;
        --padding-start: 10px;
      }

      .sale-toolbar {
        grid-template-columns: 1fr;
      }

      .product-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

    .product-card {
      min-height: 230px;
    }
  }

    @media (min-width: 761px) and (max-width: 1100px) {
      .product-grid {
        grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
      }

      .product-card {
        min-height: 260px;
      }

      .product-art {
        height: 150px;
      }
    }
  `,
})
export class SalePageComponent implements OnDestroy {
  @ViewChild('scannerPreview') scannerPreview?: ElementRef<HTMLVideoElement>;

  readonly store = inject(PosStoreService);
  readonly printing = inject(PosPrintingService);
  private readonly route = inject(ActivatedRoute);
  readonly selectedProduct = signal<PosProduct | null>(null);
  selectedQuantity = 1;
  readonly selectedAddons = signal<PosProductAddon[]>([]);
  readonly scannerOpen = signal(false);
  readonly scannerManualCode = signal('');
  readonly scannerMessage = signal('');
  readonly scannerMatches = signal<PosProduct[]>([]);
  readonly scannerLoading = signal(false);
  readonly checkoutOpen = signal(false);
  readonly mobileCartOpen = signal(false);
  readonly customerOpen = signal(false);
  readonly selectedCustomer = signal<PosClient | null>(null);
  readonly customerSearchQuery = signal('');
  readonly customerSearchResults = signal<PosClient[]>([]);
  readonly customerSearchMessage = signal('');
  readonly customerSearchLoading = signal(false);
  readonly customerSaving = signal(false);
  readonly customerName = signal('');
  readonly customerPhone = signal('');
  readonly customerEmail = signal('');
  readonly customerNotes = signal('');
  readonly paymentMethod = signal<'cash' | 'transfer'>('cash');
  readonly paymentAmount = signal(0);
  readonly abs = Math.abs;

  readonly productOpen = computed(() => this.selectedProduct() !== null);
  private scannerReader: BrowserMultiFormatReader | null = null;
  private scannerControls: IScannerControls | null = null;
  private scannerLastCode = '';

  constructor() {
    void this.preparePage();
  }

  ngOnDestroy(): void {
    this.stopScanner();
  }

  private async preparePage(): Promise<void> {
    await Promise.all([
      this.store.ensureReadyForSale(),
      this.store.setCashScope('pos'),
    ]);

    if (this.route.snapshot.data['checkout']) {
      this.openCheckout();
    }
  }

  visibleCategories() {
    return this.store.categories();
  }

  async openProduct(product: PosProduct): Promise<void> {
    this.selectedProduct.set(product);
    this.selectedQuantity = 1;
    this.selectedAddons.set([]);
    this.selectedProduct.set(await this.store.loadProductDetails(product));
  }

  closeProduct(): void {
    this.selectedProduct.set(null);
  }

  isAddonSelected(addon: PosProductAddon): boolean {
    return this.selectedAddons().some((selected) => selected.id === addon.id);
  }

  toggleAddon(addon: PosProductAddon): void {
    if (this.isAddonSelected(addon)) {
      this.selectedAddons.update((addons) => addons.filter((selected) => selected.id !== addon.id));
      return;
    }

    this.selectedAddons.update((addons) => [...addons, addon]);
  }

  addSelectedProduct(): void {
    const product = this.selectedProduct();

    if (!product) {
      return;
    }

    this.store.addToCart(product, Number(this.selectedQuantity) || 1, this.selectedAddons());
    this.closeProduct();
  }

  openScanner(): void {
    this.scannerOpen.set(true);
    this.scannerManualCode.set('');
    this.scannerMatches.set([]);
    this.scannerMessage.set('Apunta la camara al codigo de barras.');
    this.scannerLastCode = '';

    setTimeout(() => void this.startScanner(), 120);
  }

  closeScanner(): void {
    this.stopScanner();
    this.scannerOpen.set(false);
    this.scannerLoading.set(false);
  }

  async submitManualScan(): Promise<void> {
    await this.handleScannedCode(this.scannerManualCode());
  }

  async selectScannedProduct(product: PosProduct): Promise<void> {
    this.scannerLoading.set(true);

    try {
      const detailedProduct = await this.store.loadProductDetails(product);
      this.closeScanner();

      if (detailedProduct.addons.length > 0) {
        await this.openProduct(detailedProduct);
        return;
      }

      this.store.addToCart(detailedProduct);
    } finally {
      this.scannerLoading.set(false);
    }
  }

  private async startScanner(): Promise<void> {
    const video = this.scannerPreview?.nativeElement;

    if (!video || typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      this.scannerMessage.set('La camara no esta disponible en este navegador. Captura el codigo manualmente.');
      return;
    }

    try {
      if (!this.scannerReader) {
        const { BrowserMultiFormatReader } = await import('@zxing/browser');

        this.scannerReader = new BrowserMultiFormatReader();
      }

      this.scannerControls = await this.scannerReader.decodeFromConstraints(
        {
          video: {
            facingMode: { ideal: 'environment' },
          },
        },
        video,
        (result) => {
          const text = result?.getText()?.trim();

          if (!text || text === this.scannerLastCode) {
            return;
          }

          this.scannerLastCode = text;
          this.stopScanner();
          void this.handleScannedCode(text);
        },
      );
    } catch {
      this.scannerMessage.set('No se pudo activar la camara. Revisa permisos o captura el codigo manualmente.');
    }
  }

  private stopScanner(): void {
    this.scannerControls?.stop();
    this.scannerControls = null;

    const stream = this.scannerPreview?.nativeElement.srcObject;

    if (stream instanceof MediaStream) {
      stream.getTracks().forEach((track) => track.stop());
      this.scannerPreview!.nativeElement.srcObject = null;
    }
  }

  private async handleScannedCode(code: string): Promise<void> {
    const normalizedCode = code.trim();

    if (!normalizedCode || this.scannerLoading()) {
      return;
    }

    this.scannerManualCode.set(normalizedCode);
    this.scannerMatches.set([]);
    this.scannerLoading.set(true);
    this.scannerMessage.set('Buscando producto...');

    try {
      const matches = await this.store.findProductsByScan(normalizedCode);

      if (matches.length === 0) {
        this.store.setSearch(normalizedCode);
        this.scannerMessage.set('No encontre un producto exacto. Deje el codigo en la busqueda para revisar coincidencias.');
        return;
      }

      if (matches.length === 1) {
        await this.selectScannedProduct(matches[0]);
        return;
      }

      this.scannerMatches.set(matches);
      this.scannerMessage.set('Este codigo coincide con varios productos. Elige uno para agregarlo.');
    } finally {
      this.scannerLoading.set(false);
    }
  }

  openCheckout(): void {
    this.mobileCartOpen.set(false);
    this.paymentAmount.set(this.store.cartSubtotal());
    this.checkoutOpen.set(true);
  }

  async completeSale(): Promise<void> {
    this.checkoutOpen.set(false);
    this.mobileCartOpen.set(false);

    try {
      await this.store.completeSale(this.paymentAmount() || this.store.cartSubtotal(), this.paymentMethod(), this.customerPayload());
      await this.autoPrintLastOrder();
      this.paymentAmount.set(0);
      this.clearCustomer();
    } catch {
      this.checkoutOpen.set(true);
      // El store expone el error para la UI.
    }
  }

  async saveWithoutPayment(): Promise<void> {
    this.checkoutOpen.set(false);
    this.mobileCartOpen.set(false);

    try {
      await this.store.saveWithoutPayment(this.customerPayload());
      await this.autoPrintLastOrder();
      this.paymentAmount.set(0);
      this.clearCustomer();
    } catch {
      this.checkoutOpen.set(true);
      // El store expone el error para la UI.
    }
  }

  productTypeLabel(type: PosProduct['type']): string {
    return {
      bundle: 'Kit',
      custom: 'Personalizado',
      product: 'Producto',
      service: 'Servicio',
      supply: 'Insumo',
    }[type];
  }

  customerLabel(): string {
    return this.customerName().trim() || this.customerPhone().trim() || this.customerEmail().trim() || 'Publico en general';
  }

  private async autoPrintLastOrder(): Promise<void> {
    const config = this.printing.printerConfig();
    const order = this.store.lastOrder();

    if (!config?.autoPrintReceipt || !order) {
      return;
    }

    try {
      await this.printing.printReceipt(order, {
        businessName: this.receiptBusinessName(),
        currency: this.store.selectedWorkspace().currency,
      });
    } catch {
      // La venta ya fue guardada; la impresion se puede reintentar desde Ventas recientes.
    }
  }

  private receiptBusinessName(): string {
    const workspace = this.store.selectedWorkspace();

    return workspace.posSettings?.posName || workspace.name;
  }

  async searchCustomers(): Promise<void> {
    const query = this.customerSearchQuery().trim();

    if (query.length < 2) {
      this.customerSearchResults.set([]);
      this.customerSearchMessage.set('Captura al menos 2 caracteres para buscar.');
      return;
    }

    this.customerSearchLoading.set(true);
    this.customerSearchMessage.set('');

    try {
      const clients = await this.store.searchClients(query);
      this.customerSearchResults.set(clients);
      this.customerSearchMessage.set(clients.length === 0 ? 'No encontre clientes con esa busqueda.' : '');
    } finally {
      this.customerSearchLoading.set(false);
    }
  }

  selectCustomer(client: PosClient): void {
    this.selectedCustomer.set(client);
    this.customerName.set(client.name);
    this.customerPhone.set(client.phone || '');
    this.customerEmail.set(client.email || '');
    this.customerNotes.set(client.notes || '');
    this.customerSearchResults.set([]);
    this.customerSearchMessage.set('');
    this.customerOpen.set(false);
  }

  updateCustomerField(field: 'name' | 'phone' | 'email' | 'notes', value: string): void {
    this.selectedCustomer.set(null);

    if (field === 'name') {
      this.customerName.set(value);
    } else if (field === 'phone') {
      this.customerPhone.set(value);
    } else if (field === 'email') {
      this.customerEmail.set(value);
    } else {
      this.customerNotes.set(value);
    }
  }

  async saveCustomer(): Promise<void> {
    if (this.selectedCustomer()) {
      this.customerOpen.set(false);
      return;
    }

    const payload = {
      name: this.customerName().trim() || undefined,
      phone: this.customerPhone().trim() || undefined,
      email: this.customerEmail().trim() || undefined,
      notes: this.customerNotes().trim() || undefined,
    };

    if (!Object.values(payload).some(Boolean)) {
      this.customerOpen.set(false);
      return;
    }

    this.customerSaving.set(true);
    this.customerSearchMessage.set('');

    try {
      const client = await this.store.createClient(payload);
      this.selectedCustomer.set(client);
      this.customerName.set(client.name);
      this.customerPhone.set(client.phone || '');
      this.customerEmail.set(client.email || '');
      this.customerNotes.set(client.notes || payload.notes || '');
      this.customerOpen.set(false);
    } catch {
      this.customerSearchMessage.set(this.store.errorMessage() || 'No se pudo guardar el cliente.');
    } finally {
      this.customerSaving.set(false);
    }
  }

  clearCustomer(): void {
    this.selectedCustomer.set(null);
    this.customerSearchQuery.set('');
    this.customerSearchResults.set([]);
    this.customerSearchMessage.set('');
    this.customerName.set('');
    this.customerPhone.set('');
    this.customerEmail.set('');
    this.customerNotes.set('');
    this.customerOpen.set(false);
  }

  private customerPayload(): PosOrderCustomerPayload | undefined {
    const selectedCustomer = this.selectedCustomer();
    const payload: PosOrderCustomerPayload = {
      id: selectedCustomer?.id,
      uuid: selectedCustomer?.uuid,
      name: this.customerName().trim() || undefined,
      phone: this.customerPhone().trim() || undefined,
      email: this.customerEmail().trim() || undefined,
      notes: this.customerNotes().trim() || undefined,
    };

    return Object.values(payload).some(Boolean) ? payload : undefined;
  }
}
